
import * as React from 'react';
import { ISelection } from 'office-ui-fabric-react/lib/Selection';
import { Tile, ITileProps, TileSize, getTileLayout, renderTileWithLayout } from '@uifabric/experiments/lib/Tile';
import { FolderCover, renderFolderCoverWithLayout, getFolderCoverLayout } from '@uifabric/experiments/lib/FolderCover';
import { SignalField, SharedSignal } from '@uifabric/experiments/lib/components/signals/Signals';
import { BaseText } from '../fields/baseText/BaseTextField';
import * as ItemTileStyles from './ItemTile.scss';
import { ItemFileTypeIcon } from '../itemFileTypeIcon/ItemFileTypeIcon';
import { getLifeCycleSignals, getNewSignal, ISignalData } from '../fields/signal/SignalIcon';
import { itemAriaLabelControl } from '../../controls/itemAriaLabelControl';
import { getRowAriaLabel as defaultGetRowAriaLabel } from '../../models/items/AriaHelper';
import { ItemThumbnail } from '../itemThumbnail/ItemThumbnail';
import { WithItemCache, IItemCacheStateProps } from '../withItemCache/WithItemCache';
import { css } from '@uifabric/utilities/lib/css';
import { displayNameFacet, dateModifiedFacet, folderFacet, isSharedFacet } from '../../facets/ItemFacets';
import { isGroupFacet, countFacet } from '../../facets/GroupFacets';
import { thumbnailFacet } from '../../facets/MediaFacets';
import { IDecoratedProps } from '../../utilities/components/Decorators';
import { getItemHoverCard } from '../fileHoverCard/getItemHoverCard';

/**
 * @internal
 */
export interface ISelectionContext {
  selection: ISelection;
}

/**
 * @public
 */
export type IItemTileProps = IDecoratedProps<
  ITileProps,
  Pick<ITileProps, 'itemName' | 'ariaLabel' | 'itemActivity' | 'foreground' | 'showForegroundFrame'>, {
    itemKey: string;
  }>;

/**
 * @public
 */
export interface IItemTileState {
  thumbnailFailed?: boolean;
  thumbnailLoaded?: boolean;
}

const SIZES: {
  [P in TileSize]: {
    iconSize: 48 | 64;
  }
} = {
  small: {
    iconSize: 48
  },
  large: {
    iconSize: 64
  }
};

/**
 * @public
 */
export class ItemTile extends React.PureComponent<IItemTileProps, IItemTileState> {
  constructor(props: IItemTileProps) {
    super(props);
    this.state = {};
  }

  public render(): JSX.Element {
    const {
      itemKey,
      className,
      ...tileProps
    } = this.props;

    const {
      thumbnailLoaded,
      thumbnailFailed
    } = this.state;

    return (
      <WithItemCache>
        { ({ itemCache }: IItemCacheStateProps) => {
          const {
            displayName,
            dateModified,
            folder,
            isGroup,
            count,
            isShared
          } = itemCache.demandItem({
            displayName: displayNameFacet,
            dateModified: dateModifiedFacet,
            folder: folderFacet,
            isGroup: isGroupFacet,
            count: countFacet,
            isShared: isSharedFacet
          }, itemKey);

          let lifeCycleSignalData = getLifeCycleSignals(itemCache, itemKey, 1, false/*includeNew*/);
          let lifeCycleSignal = lifeCycleSignalData && lifeCycleSignalData.map((signalData: ISignalData) => {
            return signalData.signal;
          });

          if (!lifeCycleSignal) {
            lifeCycleSignal = [getNewSignal(itemCache, itemKey, '0')];
          }

          const isThumbnailResolved = itemCache.isItemFacetResolved(thumbnailFacet, itemKey);
          const thumbnail = isThumbnailResolved ? itemCache.demandItemFacet(thumbnailFacet, itemKey) : undefined;

          const nameWithSignal = (
            <SignalField
              signalsFieldMode='compact'
              before={ lifeCycleSignal }
            >
              <BaseText
                className={ ItemTileStyles.name }
                text={ displayName || '' }
                title={ displayName || '' }
              />
            </SignalField>
          );

          // Get aria label for the tile. Can use default aria label or override in addons.
          const controlHandler = itemCache.demandItemFacet(itemAriaLabelControl, itemKey);
          const defaultLabel = defaultGetRowAriaLabel(itemCache, { itemKey: itemKey });
          const { itemAriaLabel } = itemAriaLabelControl.evaluate(controlHandler)(itemCache, {
            itemKey: itemKey,
            itemAriaLabel: defaultLabel
          });

          const tile = (
            <Tile
              className={ css(ItemTileStyles.tile, className, {
                [ItemTileStyles.hideThumbnail]: !thumbnailLoaded || !!thumbnailFailed,
                [ItemTileStyles.hideFileTypeIcon]: !!thumbnailLoaded || !!thumbnail && !thumbnailFailed
              }) }
              itemName={ nameWithSignal }
              ariaLabel={ itemAriaLabel }
              data-automationid={ 'ItemTile' }
              itemActivity={
                typeof dateModified === 'number' ? (
                  <SignalField>{ new Date(dateModified).toLocaleDateString() }</SignalField>
                ) : null
              }
              { ...tileProps }
              showForegroundFrame={ !folder }
              hideForeground={
                (!!thumbnail && !thumbnailLoaded)
              }
              foreground={ <div /> }
            />
          );

          const {
            foregroundSize = { width: 0, height: 0 }
          } = getTileLayout(tile);

          const foreground: JSX.Element[] = [];

          if (folder || isGroup) {
            const folderCover = (
              <FolderCover
                key='folder'
                className={ ItemTileStyles.folderCover }
                folderCoverSize={ this.props.tileSize }
                folderCoverType={ 'default' }
                metadata={ count }
                signal={ this._getFolderCoverSignal(isShared) }
              />
            );

            const {
              contentSize
            } = getFolderCoverLayout(folderCover);

            foreground.push(
              renderFolderCoverWithLayout(folderCover, {
                children: count ? (
                  <span
                    className={ ItemTileStyles.blankFolderCover }
                    style={
                      {
                        width: `${contentSize.width}px`,
                        height: `${contentSize.height}px`
                      }
                    }
                  />
                ) : null
              })
            );
          } else {
            if (!thumbnailFailed && (!isThumbnailResolved || thumbnailLoaded)) {
              foreground.push(
                <ItemThumbnail
                  key='thumbnail'
                  itemKey={ itemKey }
                  className={ ItemTileStyles.thumbnail }
                  desiredSize={ foregroundSize }
                  onError={ this._onThumbnailError }
                  onLoad={ this._onThumbnailLoad }
                />
              );
              foreground.push(
                <ItemFileTypeIcon
                  key='fileTypeIcon'
                  itemKey={ itemKey }
                  className={ !!thumbnailLoaded ? ItemTileStyles.tileAppIcon : ItemTileStyles.fileTypeIcon }
                  size={ !!thumbnailLoaded ? 16 : SIZES[this.props.tileSize!].iconSize }
                  preferAppIcon={ false }
                />
              );
            } else {
              if (!isThumbnailResolved || !thumbnailLoaded) {
                const {
                  tileSize = 'large'
                } = this.props;
                foreground.push(
                  <ItemFileTypeIcon
                    key='fileTypeIcon'
                    itemKey={ itemKey }
                    className={ ItemTileStyles.fileTypeIcon }
                    preferAppIcon={ false }
                    size={ SIZES[tileSize].iconSize }
                  />
                );
              }
            }
          }

          const HoverCard = getItemHoverCard(itemCache, { itemKey });

          return (
            <HoverCard itemKey={ itemKey }>
              {
                renderTileWithLayout(tile, {
                  foreground: foreground.length > 0 ? foreground : null
                })
              }
            </HoverCard>
          );
        } }
      </WithItemCache>
    );
  }

  public componentWillReceiveProps(nextProps: IItemTileProps): void {
    if (nextProps.itemKey !== this.props.itemKey) {
      this.setState({
        thumbnailLoaded: false,
        thumbnailFailed: false
      });
    }
  }

  private _onThumbnailLoad = (): void => {
    this.setState({
      thumbnailLoaded: true
    });
  }

  private _onThumbnailError = (): void => {
    this.setState({
      thumbnailFailed: true
    });
  }

  private _getFolderCoverSignal(isShared: boolean | undefined): React.ReactNode {
    return (
      isShared ? <SharedSignal /> : null
    );
  }
}
